import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'

import { format, subDays } from 'date-fns'
import { de, enUS } from 'date-fns/locale'
import { toast } from 'sonner'

import { PayPalCheckoutButtons } from '@/components/paypal-checkout-buttons'
import { Button } from '@/components/ui/button'
import { EXTRAS, generateRandomPassword, PACKAGES } from '@/constants'
import { useAppSelector } from '@/hooks'
import { capitalizeFirstLetter, normalizeIban } from '@/lib/helper'
import { flushLogs, getLogFileName, recordLog } from '@/lib/logger'
import { PAYPAL_SCRIPT_OPTIONS } from '@/lib/paypal'
import { BookShelfForm } from '@/pages/customer/book-shelf'
import authService from '@/services/auth-service'
import bookingService from '@/services/booking-service'
import standService from '@/services/stand-service'

interface OverviewFormProps {
	couponDetail: Coupon | null
	referralDetail: any
}

export const OverviewForm = ({ couponDetail, referralDetail }: OverviewFormProps) => {
	const navigate = useNavigate()
	const form = useFormContext<BookShelfForm>()
	const auth = useAppSelector(state => state.auth)

	const [hasZeroPice, setHasZeroPrice] = useState(false)
	const [logFileName] = useState(getLogFileName())

	const record = (msg: string, data?: any) => recordLog(logFileName, msg, data)

	const { mutate: sendBookingEmail } = useMutation({
		mutationKey: 'send-email',
		mutationFn: (data: any) => bookingService.sendBookingEmail(data),
	})

	const { mutate: registerUser } = useMutation({
		mutationKey: 'register-user',
		mutationFn: (data: any) => authService.registerUser(data),
	})

	const { mutate: placeBooking } = useMutation({
		mutationKey: 'place-order',
		mutationFn: (data: any) => bookingService.placeOrder(data),
	})

	const userDetails = {
		firstName: form.getValues('firstName'),
		lastName: form.getValues('lastName'),
		address: form.getValues('address'),
		country: form.getValues('country'),
		zip: form.getValues('zip'),
		iban: form.getValues('iban'),
		email: form.getValues('email'),
		phoneNo: form.getValues('phoneNumber'),
	}

	const isCouponApplied = !!couponDetail
	const isReferralCodeApplied = !!referralDetail
	const selectedPackage = PACKAGES.find(p => p.id === Number(form.watch('package')))!
	const selectedExtra = EXTRAS.find(e => e.id === Number(form.watch('extras')))!

	const basePrice = Number(selectedPackage.price || 0)
	const extraPrice = Number(selectedExtra.price || 0)
	const totalPrice = basePrice + extraPrice

	const getFinalPrice = (finalPrice: string) => {
		const originalPrice = parseFloat(finalPrice)
		const extraPrice = Number(selectedExtra.price || 0)
		const basePrice = originalPrice - extraPrice

		let discountedBasePrice = basePrice

		if (couponDetail && couponDetail.discount_amount > 0) {
			const couponDiscount =
				couponDetail.discount_type === 'fixed'
					? couponDetail.discount_amount
					: basePrice * (couponDetail.discount_amount / 100)
			discountedBasePrice -= couponDiscount
		}

		if (referralDetail && referralDetail.discount_amount > 0) {
			const referralDiscount =
				referralDetail.discount_type === 'fixed'
					? referralDetail.discount_amount
					: basePrice * (referralDetail.discount_amount / 100)
			discountedBasePrice -= referralDiscount
		}

		const finalAmount = discountedBasePrice + extraPrice
		return Math.max(finalAmount, 0).toFixed(2)
	}

	const isSpecialMonth = (date: Date) => {
		const month = format(date, 'MMMM')
		return month === 'September' || month === 'October'
	}
	const isSpecialPackage = selectedPackage.id === 5

	const formatPrice = (price: number) => `${Number(price).toFixed(2).replace('.', ',')} €`

	const getRegalmietePrice = (_selectedStartDate: Date) => {
		const formattedPrice = formatPrice(selectedPackage.price)
		const salesCommission = ' + 45 % Verkaufsprovision'

		if (isSpecialPackage) {
			return formattedPrice + salesCommission
		}

		return formattedPrice
	}

	const finalGesamtpreisPrice = isSpecialMonth(form.watch('startDate'))
		? selectedPackage.id === 5
			? `${formatPrice(basePrice)} + 45 % Verkaufsprovision`
			: `${formatPrice(basePrice)}`
		: selectedPackage.id === 5
			? `${formatPrice(totalPrice)} + 45 % Verkaufsprovision`
			: `${formatPrice(totalPrice)}`

	const displayGesamtpreisPrice =
		isCouponApplied || isReferralCodeApplied
			? getFinalPrice(finalGesamtpreisPrice)
			: finalGesamtpreisPrice

	const calculateFinalPrice = (): number => {
		let basePrice =
			selectedPackage.id === 5
				? selectedPackage.price + (selectedExtra.price || 0) + 0
				: selectedPackage.price + (selectedExtra.price || 0)

		return isCouponApplied || isReferralCodeApplied
			? Number(getFinalPrice(basePrice.toString()))
			: basePrice
	}

	useEffect(() => {
		setHasZeroPrice(calculateFinalPrice() === 0)
	}, [])

	const verifyAndProceed = async (callback: Function) => {
		const standId = form.getValues('stand.id')
		const start = form.getValues('startDate')
		const end = form.getValues('endDate')

		const startUTC = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()))
		startUTC.setUTCHours(0, 0, 0, 0)

		const endUTC = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()))
		endUTC.setUTCHours(23, 59, 0, 0)

		try {
			const result = await standService.verifyStandAvailability(standId, startUTC, endUTC)

			if (!result.is_available) {
				toast.error('Der ausgewählte Stand ist leider nicht mehr verfügbar.')
				return false
			}

			return callback()
		} catch (error) {
			toast.error('Verfügbarkeit konnte nicht überprüft werden. Bitte versuchen Sie es erneut.')
			console.error('Stand verification error:', error)
			return false
		}
	}

	const createOrder = async (_data: any, actions: any): Promise<string> => {
		const price = calculateFinalPrice()
		return verifyAndProceed(async () => {
			record('Creating PayPal order', { price, selectedPackage, selectedExtra })
			return actions.order.create({
				purchase_units: [
					{
						description: `Booking stand ${selectedPackage.title}`,
						amount: { currency_code: 'EUR', value: price.toFixed(2) },
					},
				],
				application_context: { shipping_preference: 'NO_SHIPPING' },
			})
		})
	}

	const onApprove = async (_data: any, actions: any): Promise<void> => {
		const details = await actions.order.capture()
		const paypalCode = details?.purchase_units?.[0]?.payments?.captures?.[0]?.id
		record('PayPal payment captured', details)

		if (paypalCode) {
			const bookingSnapshot = buildBookingPayload(paypalCode)
			record('Payment successful — storing booking payload snapshot', bookingSnapshot)
			try {
				await placeOrder(paypalCode)
				await sendEmail(false)
			} catch (err) {
				record('Booking failed after payment', err)
				await flushLogs(logFileName)
			}
		}
	}

	/** Apple Pay captures server-side, so it hands back the capture id directly. */
	const onApplePayApproved = async (captureId: string): Promise<void> => {
		record('Apple Pay payment captured', { captureId })
		const bookingSnapshot = buildBookingPayload(captureId)
		record('Payment successful — storing booking payload snapshot', bookingSnapshot)

		try {
			await placeOrder(captureId)
			await sendEmail(false)
		} catch (err) {
			record('Booking failed after payment', err)
			await flushLogs(logFileName)
		}
	}

	const sendEmail = async (shouldPlaceOrder: boolean) => {
		const { is_available, ...rest } = form.getValues('stand')
		const payload = {
			bookingStand: JSON.stringify(selectedPackage.title),
			userDetails: JSON.stringify(userDetails),
			selectedStartDate: JSON.stringify(form.getValues('startDate')),
			selectedEndDate: JSON.stringify(form.getValues('endDate')),
			selectedPackage: JSON.stringify(selectedPackage),
			extraItem: JSON.stringify(selectedExtra),
			selectedDayItem: JSON.stringify(selectedPackage),
			selectedStand: JSON.stringify({ ...rest, isAvailable: is_available }),
			newsOffer: JSON.stringify(form.getValues('receiveNews')),
		}

		sendBookingEmail(payload, {
			onSuccess: res => {
				if (!res || (res.status !== 200 && res.message !== 'Email sent successfully!')) {
					toast.error('Confirmation email could not be sent!')
				}
			},
			onError: () => {
				toast.error('Confirmation email could not be sent!')
			},
		})

		if (shouldPlaceOrder) placeOrder()
	}

	const buildBookingPayload = (paypalCode?: string) => {
		const price = calculateFinalPrice()
		const formatLocalDate = (date: Date) =>
			`${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`
		return {
			shelf_type: 1,
			stand_id: form.getValues('stand.id'),
			paypal_code: paypalCode,
			lock_token: form.getValues('lock_token') ?? null,
			duration: selectedPackage.duration,
			start: formatLocalDate(new Date(format(form.getValues('startDate'), 'yyyy-MM-dd'))),
			end: formatLocalDate(new Date(format(form.getValues('endDate'), 'yyyy-MM-dd'))),
			collect_product_by: selectedExtra.title,
			rebook: 0,
			rent_price: price,
			original_price: price,
			status: 'active',
			prepare: format(subDays(form.getValues('startDate'), 1), 'yyyy-MM-dd', { locale: enUS }),
			user_id: auth?.id ?? null,
			description: '',
			coupon: couponDetail?.code ?? null,
			referral_code: referralDetail ? form.getValues('referralCode') : null,
		}
	}

	const placeOrder = async (paypalCode?: string) => {
		const bookingPayload = buildBookingPayload(paypalCode)
		record('Placing booking', bookingPayload)

		const handleSuccess = async (response: any) => {
			record('Booking API success', response)
			const bookingId = response?.data?.id
			if (bookingId) record(`Booking created successfully with ID: ${bookingId}`)
			await flushLogs(logFileName, bookingId)
			navigate('/booking-success')
		}

		const handleError = async (error: any) => {
			record('Booking API error', error)
			toast.error(error?.response?.data?.message ?? error?.message)
			await flushLogs(logFileName)
		}

		if (!auth?.id) {
			const passwordString = generateRandomPassword(10)
			const newUser = {
				first_name: userDetails.firstName,
				last_name: userDetails.lastName,
				email: userDetails.email,
				status: 'active',
				password: passwordString,
				confirm_password: passwordString,
				country: userDetails.country,
				zip: userDetails.zip,
				iban: normalizeIban(userDetails.iban),
				address: userDetails.address,
				phone_number: userDetails.phoneNo,
				news_offer: form.getValues('receiveNews'),
			}
			record('Registering new user', newUser)
			registerUser(newUser, {
				onSuccess: res => {
					record('User registered', res)
					if (res?.data?.status) {
						const updatedPayload = { ...bookingPayload, user_id: res?.data?.id }
						placeBooking(updatedPayload, { onSuccess: handleSuccess, onError: handleError })
					}
				},
				onError: handleError,
			})
		} else {
			placeBooking(bookingPayload, { onSuccess: handleSuccess, onError: handleError })
		}
	}

	return (
		<div className="mt-10 lg:mt-12 flex flex-col">
			<h5 className="text-secondary/50 max-lg:text-xs font-medium">Zahlungszusammenfassung</h5>
			<div className="mt-6 lg:mt-8 border border-[#E7E9ED] rounded-xl lg:rounded-[16px] px-3 py-4 lg:p-6 flex flex-col gap-5 lg:gap-8">
				<h1 className="text-secondary font-extrabold text-sm lg:text-xl">Deine Daten</h1>
				<div className="flex flex-col gap-3 lg:gap-4">
					<div className="flex items-center justify-between pb-3 gap-[9px] lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Name</p>
						<p className="text-secondary font-extrabold max-lg:text-xs">
							{capitalizeFirstLetter(form.watch('firstName')) +
								' ' +
								capitalizeFirstLetter(form.watch('lastName'))}
						</p>
					</div>
					<div className="flex items-center justify-between pb-3 gap-[9px] lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Adresse</p>
						<p className="text-secondary font-extrabold max-lg:text-xs">
							{capitalizeFirstLetter(form.watch('address'))}
						</p>
					</div>
					<div className="flex items-center justify-between pb-3 gap-[9px] lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Ort</p>
						<p className="text-secondary font-extrabold max-lg:text-xs">{form.watch('country')}</p>
					</div>
					<div className="flex items-center justify-between gap-[9px]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Postleitzahl</p>
						<p className="text-secondary font-extrabold max-lg:text-xs">{form.watch('zip')}</p>
					</div>
				</div>
			</div>
			<div className="mt-8 border border-[#E7E9ED] rounded-[16px] p-6 flex flex-col gap-8">
				<h1 className="text-secondary font-extrabold max-lg:text-sm text-xl">
					Details zu deiner Buchung
				</h1>
				<div className="flex flex-col gap-4">
					<div className="flex items-center justify-between pb-3 gap-[9px] lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Mietbeginn</p>
						<p className="text-secondary font-extrabold max-lg:text-xs">
							{format(form.watch('startDate'), 'dd. MMMM yyyy', { locale: de })}
						</p>
					</div>
					<div className="flex items-center gap-4 justify-between pb-3 lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Einräumen</p>
						<p className="text-secondary text-right font-extrabold max-lg:text-xs">
							frühestens am Werktag vor deinem Mietbeginn eine Stunde vor Ladenschluss und täglich
							während der Öffnungszeiten ab 10:00 Uhr
						</p>
					</div>
					<div className="flex items-center justify-between pb-3 gap-[9px] lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Mietende</p>
						<p className="text-secondary font-extrabold max-lg:text-xs">
							{format(form.watch('endDate'), 'dd. MMMM yyyy', { locale: de })}
						</p>
					</div>
					<div className="flex items-center justify-between pb-3 gap-[9px] lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Ausräumen</p>
						<p className="text-secondary text-right font-extrabold max-lg:text-xs">
							bis spätestens {format(form.watch('endDate'), 'dd. MMMM yyyy', { locale: de })} eine
							Stunde vor Ladenschluss
						</p>
					</div>
					<div className="flex items-center justify-between pb-3 gap-[9px] lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Paket</p>
						<p className="text-secondary font-extrabold max-lg:text-xs">
							{EXTRAS.find(extra => extra.id === Number(form.watch('extras')))?.title}
						</p>
					</div>
					<div className="flex items-center justify-between pb-3 gap-[9px] lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Regalmiete</p>
						<p className="text-secondary text-right font-extrabold max-lg:text-xs">
							{getRegalmietePrice(form.watch('startDate'))}
						</p>
					</div>
					<div className="flex items-center justify-between pb-3 gap-[9px] lg:pb-4 border-b border-[#E7E9ED]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Extra-Service</p>
						<p className="text-secondary font-extrabold max-lg:text-xs">
							{selectedExtra.price ? formatPrice(selectedExtra.price) : '0,00 €'}
						</p>
					</div>
					<div className="flex items-center justify-between gap-[9px]">
						<p className="max-lg:text-xs text-secondary/50 font-medium">Gesamtpreis</p>
						<p className="text-secondary font-extrabold max-lg:text-xs">
							{displayGesamtpreisPrice}
						</p>
					</div>
				</div>
			</div>
			<div className="mt-10 lg:mt-8">
				<h5 className="max-lg:text-sm text-secondary lg:text-center font-medium">Zur Zahlung:</h5>
				<div className="mt-3 lg:mt-5">
					{hasZeroPice || (selectedPackage.id === 5 && selectedExtra.id === 1) ? (
						<Button
							onClick={() => verifyAndProceed(() => sendEmail(true))}
							type="button"
							variant="secondary"
							className="lg:h-14 rounded-xl w-full">
							Buchung abschließen
						</Button>
					) : (
						<PayPalScriptProvider options={PAYPAL_SCRIPT_OPTIONS}>
							<PayPalCheckoutButtons
								createOrder={createOrder}
								onApprove={onApprove}
								applePay={{
									amount: calculateFinalPrice(),
									label: `Booking stand ${selectedPackage.title}`,
									onBeforePay: () => verifyAndProceed(() => true),
									onApproved: onApplePayApproved,
								}}
							/>
						</PayPalScriptProvider>
					)}
				</div>
			</div>
		</div>
	)
}
